import dayjs from "dayjs"
import http, { ApiRequestConfig } from "@services/httpService"
import type {
  ApprovalImportMode,
  ApprovalMatrixFilterDto,
  ApprovalMatrixOutputDto,
  ApprovalProcessExportFilterDto,
  CloneApprovalMapDto,
  CloneApprovalMapResultDto,
  ImportApprovalProcessResultDto,
} from "@services/approvalProcess/approvalProcessDto"
import { L, LNotification } from "@lib/abpUtility"
import { notifyError, notifySuccess } from "@lib/helper"
import { downloadFile } from "@lib/helperFile"
import { PagedResultDto } from "@services/dto/pagedResultDto"
import { ApprovalProcessModel } from "@models/approvalProcess/approvalProcessModel"

class ApprovalProcess {
  public async create(body: any) {
    const res = await http.post("api/services/app/Approval/Create", body)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }

  public async update(body: any) {
    const res = await http.put("api/services/app/Approval/Update", body)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }

  public async active(id: number, isActive) {
    const res = await http.post(
      "api/services/app/Approval/Active",
      { id },
      { params: { isActive } }
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("UPDATE_SUCCESSFULLY")
    )
    return res.data
  }

  /**
   * Bulk activate/deactivate the selected workflows in one atomic call (R31.4, R31.5). ABP infers
   * POST from the `Active*` prefix; the backend applies `isActive` to every id in one unit-of-work
   * (all-or-nothing). The scene owns the success toast / selection reset / refresh, so this method
   * only performs the HTTP call and leaves the legacy per-row `active(id, isActive)` unchanged.
   *
   * Parameter placement is dictated by ABP's dynamic-API convention (`AbpAppServiceConvention`):
   * only NON-primitive parameters get `[FromBody]`, primitives are bound from the query string. The
   * backend signature is `ActiveMultipleAsync(List<int> ids, bool isActive)`, so `ids` must be the
   * whole request body and `isActive` must travel as a query parameter — exactly like the existing
   * `active(id, isActive)` above. Putting both in one body object makes `ids` fail to deserialize
   * and silently leaves `isActive = false`.
   */
  public async activeMultiple(ids: number[], isActive: boolean) {
    const res = await http.post(
      "api/services/app/Approval/ActiveMultiple",
      ids,
      { params: { isActive } }
    )
    return res.data
  }

  public async get(id: number): Promise<any> {
    if (!id) {
      notifyError(L("ERROR"), L("ENTITY_NOT_FOUND"))
    }
    const res = await http.get("api/services/app/Approval/Get", {
      params: { id },
    })
    const { result } = res.data
    return ApprovalProcessModel.assign(result)
  }

  public async getAll(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get("api/services/app/Approval/GetAll", {
      params,
    })
    return res.data.result
  }

  public async getMatrix(
    params: ApprovalMatrixFilterDto
  ): Promise<ApprovalMatrixOutputDto> {
    const res = await http.get<{ result: ApprovalMatrixOutputDto }>(
      "api/services/app/Approval/GetMatrix",
      { params }
    )
    return res.data.result
  }

  public async cloneMapToProjects(
    body: CloneApprovalMapDto
  ): Promise<CloneApprovalMapResultDto> {
    const res = await http.post<{ result: CloneApprovalMapResultDto }>(
      "api/services/app/Approval/CloneMapToProjects",
      body
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }

  public async assignMapToProjects(
    body: CloneApprovalMapDto
  ): Promise<CloneApprovalMapResultDto> {
    const res = await http.post<{ result: CloneApprovalMapResultDto }>(
      "api/services/app/Approval/AssignMapToProjects",
      body
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }

  /**
   * Export the approval-process workbook using exactly the applied filters (R12.2, R12.3).
   * Returns "empty" when the backend answers 204 (no rows matched) so the caller can show the
   * localized empty notice without downloading a file (R12.9); otherwise saves the workbook and
   * returns "downloaded".
   */
  public async exportApprovalProcesses(
    filters: ApprovalProcessExportFilterDto
  ): Promise<"downloaded" | "empty"> {
    const res = await http.get("api/Export/ExportApprovalProcess", {
      params: {
        modules: filters.modules,
        isActive: filters.isActive,
        projectIds: filters.projectIds,
        keyword: filters.keyword,
      },
      responseType: "blob",
    })

    const blob = res.data as Blob
    if (res.status === 204 || !blob || blob.size === 0) {
      return "empty"
    }

    downloadFile(
      blob,
      `EXPORT-APPROVAL-PROCESS-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`
    )
    return "downloaded"
  }

  /**
   * Download the import template (R14.3). The export workbook doubles as the import template
   * (task 9.3), so this reuses the `Export-ApprovalProcess.xlsx` two-sheet structure via the
   * export endpoint with no applied filters. Returns "empty" when the backend answers 204 so the
   * caller can show the localized empty notice without saving a file; otherwise saves the template
   * and returns "downloaded".
   */
  public async downloadImportTemplate(): Promise<"downloaded" | "empty"> {
    const res = await http.get("api/Export/ExportApprovalProcess", {
      responseType: "blob",
    })

    const blob = res.data as Blob
    if (res.status === 204 || !blob || blob.size === 0) {
      return "empty"
    }

    downloadFile(
      blob,
      `IMPORT-APPROVAL-PROCESS-TEMPLATE-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`
    )
    return "downloaded"
  }

  /**
   * Validate an approval-process workbook before persisting (R14.4). Multipart upload; the backend
   * writes nothing and returns the per-row report, valid/invalid counts and a single-use
   * validation token bound to the chosen mode (R15.1, R15.2).
   */
  public async validateImport(
    file: File,
    mode: ApprovalImportMode
  ): Promise<ImportApprovalProcessResultDto> {
    const formData = new FormData()
    formData.append("file", file)
    const res = await http.post<{ result: ImportApprovalProcessResultDto }>(
      "api/Imports/ValidateApprovalProcess",
      formData,
      {
        headers: { "content-type": "multipart/form-data" },
        params: { mode },
      }
    )
    return res.data.result
  }

  /**
   * Persist the reviewed workbook after the user confirms (R14.9, R15.1). Multipart upload with the
   * validation token and mode from the validate step; the backend re-validates and writes the
   * eligible workflows in one unit-of-work, returning created/updated/skipped counts.
   */
  public async persistImport(
    file: File,
    token: string,
    mode: ApprovalImportMode
  ): Promise<ImportApprovalProcessResultDto> {
    const formData = new FormData()
    formData.append("file", file)
    const res = await http.post<{ result: ImportApprovalProcessResultDto }>(
      "api/Imports/ImportApprovalProcess",
      formData,
      {
        headers: { "content-type": "multipart/form-data" },
        params: { token, mode },
      }
    )
    return res.data.result
  }

  public async getList(params: any): Promise<any> {
    const res = await http.get("api/services/app/Approval/GetList", {
      params,
    })
    return res.data.result
  }

  public async getListFlow(params: any): Promise<any> {
    const res = await http.get("api/services/app/Approval/GetListFlow", {
      params,
    })
    return res.data.result
  }

  /**
   * `options.silent`: caller tự báo kết quả nên KHÔNG bật popup thành công/lỗi của lượt này.
   * Dùng cho gửi duyệt hàng loạt — mỗi sự kiện một lời gọi (đường `SendRequest` là đường DUY NHẤT
   * có chụp lại hợp đồng cho sự kiện hợp đồng), để mặc định thì người dùng nhận đúng N popup.
   */
  public async sendRequest(body: any, options?: { silent?: boolean }) {
    const res = await http.post("api/services/app/Approval/SendRequest", body, {
      skipErrorNotification: options?.silent,
    } as ApiRequestConfig)
    if (!options?.silent) {
      notifySuccess(
        LNotification("SUCCESS"),
        LNotification("SEND_APPROVAL_SUCCESSFULLY")
      )
    }
    return res.data.result
  }

  public async sendMultiRequest(body: any) {
    const res = await http.post(
      "api/services/app/Approval/SendMultiRequest",
      body
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("APPROVAL_SUCCESSFULLY")
    )
    return res.data.result
  }
}

export default new ApprovalProcess()
